import React, { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { loadStripe } from "@stripe/stripe-js";
import { z } from "zod";
import { createServerFn } from "@tanstack/react-start";
import { getLocationConfig } from "~/routes/api/leadconnector/query";
import { getStripe } from "~/lib/stripe";

const GHL_ORIGIN = "https://app.gohighlevel.com";

const PaymentSchema = z.object({
  type: z.literal("payment_initiate_props"),
  publishableKey: z.string(),
  amount: z.number().positive(),
  currency: z.string().length(3),
  orderId: z.string(),
  transactionId: z.string(),
  locationId: z.string(),
  contact: z
    .object({
      id: z.string(),
      name: z.string().optional(),
      email: z.string().email().optional(),
      contact: z.string().optional(),
    })
    .optional(),
});

const CreatePaymentIntentSchema = z.object({
  amount: z.number().min(0.5),
  currency: z.string().length(3),
  transactionId: z.string(),
  locationId: z.string(),
});

export const createPaymentIntent = createServerFn({ method: "POST" })
  .validator((data: unknown) => CreatePaymentIntentSchema.parse(data))
  .handler(async ({ data }) => {
    const stripe = await getStripe();

    // Get connected account from your database
    const { stripeAccountId, appFeePercent } = await getLocationConfig(
      data.locationId,
    );

    const applicationFee = Math.round(data.amount * appFeePercent * 100);

    const paymentIntent = await stripe.paymentIntents.create(
      {
        amount: Math.round(data.amount * 100),
        currency: data.currency,
        application_fee_amount: applicationFee,
        transfer_data: {
          destination: stripeAccountId,
        },
        metadata: {
          ghlLocationId: data.locationId,
          ghlTransactionId: data.transactionId,
        },
      },
      {
        stripeAccount: stripeAccountId,
      },
    );

    return { clientSecret: paymentIntent.client_secret! };
  });

export function getPublishableKey(): string {
  return process.env.VITE_STRIPE_PUBLISHABLE_KEY || "";
}

function PaymentIframe() {
  useEffect(() => {
    const initializePayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const clientSecret = urlParams.get("payment_intent_client_secret");
      if (!clientSecret) {
        window.parent.postMessage(
          {
            type: "custom_element_error_response",
            error: { message: "Missing client secret" },
          },
          GHL_ORIGIN,
        );

        return;
      }

      const stripe = await loadStripe(await getPublishableKey());
      if (!stripe) {
        throw new Error("Failed to load Stripe");
      }

      if (clientSecret) {
        // Handle redirect return
        const { paymentIntent, error } =
          await stripe.retrievePaymentIntent(clientSecret);

        window.parent.postMessage(
          {
            type:
              "succeeded" === paymentIntent?.status
                ? "custom_element_success_response"
                : "custom_element_error_response",
            ...(paymentIntent && { chargeId: paymentIntent.id }),
            ...(error && { error: { description: error.message } }),
          },
          GHL_ORIGIN,
        );

        return;
      }

      // Initial payment flow
      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== GHL_ORIGIN) {
          return;
        }

        const data = PaymentSchema.parse(event.data);

        // Create payment intent server-side
        const result = await createPaymentIntent({
          data: {
            amount: data.amount,
            currency: data.currency,
            transactionId: data.transactionId,
            locationId: data.locationId,
          },
        });

        if (!result.clientSecret) {
          throw new Error("Payment setup failed");
        }

        const { error, paymentIntent } = await stripe.confirmPayment({
          elements: stripe.elements(),
          clientSecret: result.clientSecret,
          confirmParams: {
            return_url: `${window.location.origin}/ghl-callback?transactionId=${data.transactionId}`,
            payment_method_data: {
              billing_details: {
                name: event.data.contact?.name,
                email: event.data.contact?.email,
              },
            },
          },
          redirect: "if_required",
        });

        if (error) {
          window.parent.postMessage(
            {
              type: "custom_element_error_response",
              error: { description: error.message || "Payment failed" },
            },
            GHL_ORIGIN,
          );
          return;
        }

        if ("requires_action" === paymentIntent.status) {
          await stripe.handleCardAction(clientSecret);
        }

        window.parent.postMessage(
          {
            type: "custom_element_success_response",
            chargeId: paymentIntent.id,
          },
          GHL_ORIGIN,
        );
      };

      window.addEventListener("message", handleMessage);

      window.parent.postMessage(
        { type: "custom_provider_ready", loaded: true },
        GHL_ORIGIN,
      );

      return () => {
        window.removeEventListener("message", handleMessage);
      };
    };

    initializePayment();
  }, []);

  return <div id="stripe-elements" />;
}

export const Route = createFileRoute("/lc-payment/")({
  component: PaymentIframe,
});
